// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Écran de tri des signalements « cet avis est faux » — S3, 25/08/2026.
//
// CE QU'IL SERT
// -------------
// Un signalement client n'est PAS de la vérité terrain tant qu'il n'a pas été
// trié : c'est une réclamation. Cet écran est l'étape qui transforme l'une en
// l'autre, en deux gestes — retenir (le signalement est fondé) ou écarter
// (l'avis rendu était juste).
//
// ⚠️ UN SIGNALEMENT ÉCARTÉ RESTE EN BASE, et c'est délibéré. Savoir qu'un
// utilisateur a contesté à tort mesure la LISIBILITÉ du rendu — c'est même la
// moitié la plus instructive du signal : elle dit où le produit se fait mal
// comprendre. L'effacer reviendrait à ne garder que ce qui nous donne raison.
//
// LE TABLEAU « RÈGLES LES PLUS CONTESTÉES »
// -----------------------------------------
// Il porte sur la TOTALITÉ des signalements, jamais sur la page affichée — c'est
// lui qui répond à « quelle règle corriger en premier », et il serait faux s'il
// ne comptait que les 50 dernières lignes. Le back le calcule pour cette raison.
//
// ⛔ TROIS POSITIONS, TROIS MOTS : Favorable / Réserve / Défavorable.
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import SkeletonRows from '../components/SkeletonRows';
import { useI18n } from '../i18n';
import {
  getAdminRuleFeedback, adminUpdateRuleFeedback, exportRuleFeedbackS0,
} from '../services/api';

const PAGE_SIZE = 50;

const TEINTE_POSITION = {
  FAVORABLE:   { bg: '#f0fdf4', text: '#166534' },
  RESERVE:     { bg: '#fefce8', text: '#854d0e' },
  DEFAVORABLE: { bg: '#fee2e2', text: '#991b1b' },
  INDECIDABLE: { bg: '#f1f5f9', text: '#475569' },
};

const TEINTE_TRAITEMENT = {
  nouveau: { bg: '#eff6ff', text: '#1d4ed8' },
  retenu:  { bg: '#f0fdf4', text: '#166534' },
  ecarte:  { bg: '#f1f5f9', text: '#475569' },
};

const AdminRetoursAvis = () => {
  const { t, lang } = useI18n();

  const [retours, setRetours]     = useState([]);
  const [parRegle, setParRegle]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [aTrier, setATrier]       = useState(0);
  // Ce que l'export produirait aujourd'hui, par feuille — affiché sur les
  // boutons pour qu'un export vide ne surprenne jamais.
  const [exportables, setExportables] = useState({ precision: 0, rappel: 0 });
  const [offset, setOffset]       = useState(0);
  const [hasMore, setHasMore]     = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]       = useState('');

  const [filtreTraitement, setFiltreTraitement] = useState('nouveau');
  const [filtreKind, setFiltreKind]             = useState('');
  // Note interne en cours de saisie, par identifiant de signalement.
  const [notes, setNotes]         = useState({});

  const POSITIONS = {
    FAVORABLE:   t('retour_pos_favorable'),
    RESERVE:     t('retour_pos_reserve'),
    DEFAVORABLE: t('retour_pos_defavorable'),
    INDECIDABLE: t('retour_pos_unknown'),
  };
  const TRAITEMENTS = {
    nouveau: t('retour_status_nouveau'),
    retenu:  t('retour_status_retenu'),
    ecarte:  t('retour_status_ecarte'),
  };

  const charger = useCallback(async (off = 0, ajouter = false) => {
    if (!ajouter) setChargement(true);
    try {
      const data = await getAdminRuleFeedback({
        traitement: filtreTraitement || undefined,
        kind: filtreKind || undefined,
        limit: PAGE_SIZE,
        offset: off,
      });
      setRetours(prev => ajouter ? [...prev, ...(data.retours || [])] : (data.retours || []));
      setParRegle(data.par_regle || []);
      setTotal(data.total || 0);
      setATrier(data.a_trier || 0);
      setExportables(data.exportables || { precision: 0, rappel: 0 });
      setHasMore(data.has_more || false);
      setOffset(off + PAGE_SIZE);
      setErreur('');
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  }, [filtreTraitement, filtreKind]);

  useEffect(() => { charger(0, false); }, [charger]);

  const trier = async (retour, traitement) => {
    try {
      await adminUpdateRuleFeedback(retour.id, {
        traitement,
        traitement_note: notes[retour.id] ?? retour.traitement_note ?? '',
      });
      await charger(0, false);
    } catch (e) {
      setErreur(e.message);
    }
  };

  const dateCourte = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? '—' : d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    });
  };

  const Etiquette = ({ valeur, libelle, teintes }) => {
    const c = teintes[valeur] || { bg: '#f1f5f9', text: '#475569' };
    return (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                     background: c.bg, color: c.text, whiteSpace: 'nowrap' }}>
        {libelle || valeur}
      </span>
    );
  };

  const styleSelect = {
    fontSize: 12, padding: '6px 8px', borderRadius: 6,
    border: '1px solid var(--gray)', background: 'var(--bg-input)',
    color: 'var(--black)', cursor: 'pointer', outline: 'none',
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <main className="container mx-auto px-4 py-8 max-w-6xl">

        {/* En-tête */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>
            {t('retour_admin_title')}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {t('retour_admin_subtitle')} · <strong>{aTrier}</strong> {t('retour_admin_to_sort')}
          </p>
        </div>

        {/* Export S0 — le raccord du chantier. Le mode d'emploi est affiché
            plutôt que documenté ailleurs : c'est une commande qu'on tape une
            fois tous les trois mois, personne ne la retiendra. */}
        <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 8,
                      background: 'rgba(128,128,128,0.05)', border: '1px solid var(--gray)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--black)' }}>
              {t('retour_admin_export')} :
            </span>
            <Button size="sm" variant="outline" onClick={() => exportRuleFeedbackS0('precision').catch(e => alert(e.message))}>
              {t('retour_admin_export_precision')} ({exportables.precision})
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportRuleFeedbackS0('rappel').catch(e => alert(e.message))}>
              {t('retour_admin_export_rappel')} ({exportables.rappel})
            </Button>
          </div>

          {/* ⚠️ UN EXPORT VIDE NE DOIT JAMAIS ÊTRE MUET. Seuls les retours
              RETENUS sortent — un retour non trié, ou écarté, est une
              réclamation, pas de la vérité terrain. Sans cet avertissement,
              l'utilisateur télécharge un fichier vide identique à un fichier
              plein et conclut à une perte de données (signalé par Marc au
              premier test manuel, 25/08/2026). */}
          {exportables.precision === 0 && exportables.rappel === 0 && (
            <p style={{ fontSize: 12, color: '#b45309', margin: '10px 0 0',
                        padding: '8px 10px', borderRadius: 6,
                        background: 'rgba(180,83,9,0.08)' }}>
              {t('retour_admin_export_empty')}
            </p>
          )}
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0', fontFamily: 'monospace' }}>
            {t('retour_admin_export_hint')}
          </p>
          <p style={{ fontSize: 11, color: '#b45309', margin: '6px 0 0' }}>
            ⚠️ {t('retour_admin_export_warn')}
          </p>
        </div>

        {/* Règles les plus contestées — calculé sur la TOTALITÉ, pas sur la page */}
        {parRegle.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)', marginBottom: 8 }}>
              {t('retour_admin_by_rule')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {parRegle.map(r => (
                <div key={`${r.norme}#${r.rule_number}`}
                     style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray)',
                              background: 'var(--bg-surface)', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: 'var(--black)' }}>
                    R{r.rule_number} — {r.rule_name}
                  </div>
                  <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                    {r.total} {t('retour_admin_by_rule_total')} · {r.retenus} {t('retour_admin_by_rule_kept')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('retour_admin_filter')} :</span>
          <select value={filtreTraitement} onChange={e => setFiltreTraitement(e.target.value)} style={styleSelect}>
            <option value="">{t('retour_admin_filter_all')}</option>
            <option value="nouveau">{t('retour_status_nouveau')}</option>
            <option value="retenu">{t('retour_status_retenu')}</option>
            <option value="ecarte">{t('retour_status_ecarte')}</option>
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('retour_admin_kind')} :</span>
          <select value={filtreKind} onChange={e => setFiltreKind(e.target.value)} style={styleSelect}>
            <option value="">{t('retour_admin_kind_all')}</option>
            <option value="position">{t('retour_admin_kind_position')}</option>
            <option value="ecart_manque">{t('retour_admin_kind_missing')}</option>
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{total}</span>
        </div>

        {erreur && (
          <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>❌ {erreur}</p>
        )}

        {chargement ? (
          <SkeletonRows />
        ) : retours.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            {t('retour_admin_empty')}
          </div>
        ) : (
          <>
            {retours.map(r => (
              <div key={r.id}
                   style={{ marginBottom: 12, padding: '14px 16px', borderRadius: 10,
                            border: '1px solid var(--gray)', background: 'var(--bg-surface)' }}>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  <Etiquette valeur={r.traitement} libelle={TRAITEMENTS[r.traitement]} teintes={TEINTE_TRAITEMENT} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{dateCourte(r.created_at)}</span>
                  <span style={{ fontSize: 11, color: 'var(--blue)' }}>{r.user_email || '—'}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}
                        title={r.filename}>
                    📄 {r.filename || r.analysis_id}
                  </span>
                </div>

                {r.kind === 'position' ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                      R{r.rule_number} — {r.rule_name}
                    </span>
                    <Etiquette valeur={r.position_rendue}
                               libelle={POSITIONS[r.position_rendue]} teintes={TEINTE_POSITION} />
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>→</span>
                    <Etiquette valeur={r.position_attendue}
                               libelle={POSITIONS[r.position_attendue]} teintes={TEINTE_POSITION} />
                  </div>
                ) : (
                  <div style={{ marginBottom: 8 }}>
                    <Etiquette valeur="ecart" libelle={t('retour_admin_kind_missing')}
                               teintes={{ ecart: { bg: '#fef3c7', text: '#92400e' } }} />
                  </div>
                )}

                {/* Le POURQUOI — la seule chose qui distingue ce signalement d'un
                    vote, et la raison pour laquelle il est obligatoire. */}
                <div style={{ fontSize: 13, color: 'var(--black)', lineHeight: 1.6,
                              background: 'rgba(128,128,128,0.06)', borderRadius: 8,
                              padding: '8px 12px', marginBottom: 10 }}>
                  {r.commentaire}
                </div>

                {/* Ce que la règle disait au moment du signalement — gelé à
                    l'écriture, donc toujours d'accord avec ce que la personne a
                    lu, même si l'analyse a été rejouée depuis. */}
                {r.ce_que_dit_la_regle && (
                  <details style={{ marginBottom: 10 }}>
                    <summary style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
                      {t('retour_admin_col_rule')}
                    </summary>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.6 }}>
                      {r.ce_que_dit_la_regle}
                    </div>
                  </details>
                )}

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={notes[r.id] ?? r.traitement_note ?? ''}
                    onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                    placeholder={t('retour_admin_note_ph')}
                    style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '6px 10px',
                             borderRadius: 6, border: '1px solid var(--gray)',
                             background: 'var(--bg-input)', color: 'var(--black)', outline: 'none' }}
                  />
                  <Button size="sm" onClick={() => trier(r, 'retenu')}
                          disabled={r.traitement === 'retenu'}>
                    {t('retour_admin_keep')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => trier(r, 'ecarte')}
                          disabled={r.traitement === 'ecarte'}>
                    {t('retour_admin_dismiss')}
                  </Button>
                </div>
              </div>
            ))}

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Button variant="outline" onClick={() => charger(offset, true)}>
                  {t('load_more')}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminRetoursAvis;
